import React, { useState } from "react";
import styles from "./learningToolVocabulary.module.css";
import AudioPlayer from "../audioPlayer/AudioPlayer";
import LearningToolButtons from "../learningToolButtons/LearningToolButtons";
import LearningUnitButtons from "../learningUnitButtons/LearningUnitButtons";
import LearningToolModal from "../learningToolModal/LearningToolModal";
import VocabularyModal from "./vocabularyModal/VocabularyModal";
import axios from "axios";

const apiUrl = process.env.REACT_APP_URL;
axios.defaults.baseURL = `${apiUrl}`;
axios.defaults.withCredentials = true;

const LearningToolVocabulary = ({
  tool,
  mayEditTool,
  removeToolFromList,
  maxUnitsPerTool,
}) => {
  /* The LearningToolVocabulary component is responsible for displaying, adding, editing, and removing a Vocabulary learning tool. Each tool may contain one or more learning units. A Vocabulary learning unit contains an original text (in the source language), a translated text (in the target language), and (optionally) a pronunciation audio - in the target language. */

  const [toolUnits, setToolUnits] = useState(tool.units);
  const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
  const [activeModalUnit, setActiveModalUnit] = useState(null);
  const [isToolModalOpen, setIsToolModalOpen] = useState(false);
  const [isUnitContentModalOpen, setIsUnitContentModalOpen] = useState(false);
  const [isNewUnitModalOpen, setIsNewUnitModalOpen] = useState(false);
  const [activeContentModalUnit, setActiveContentModalUnit] = useState(null);
  const [activeUnitIndex, setActiveUnitIndex] = useState(null);

  const learningToolId = tool.learning_tool_id;

  /**
   * Open the unit modal to enter details of a new unit
   */
  const onAddUnit = () => {
    setIsNewUnitModalOpen(true);
  };

  /**
   * Open the confirmation modal to confirm unit deletion
   * @param {*} unitId - the id of the unit to delete
   */
  const onDeleteUnit = (unitId) => {
    setActiveModalUnit(unitId);
    setIsUnitModalOpen(true);
  };

  /**
   * Open the confirmation modal to confirm the tool deletion
   */
  const onDeleteTool = () => {
    setIsToolModalOpen(true);
  };

  /**
   * Close the unit confirmation modal
   */
  const closeUnitModal = () => {
    setIsUnitModalOpen(false);
  };

  /**
   * Close the tool confirmation modal
   */
  const closeToolModal = () => {
    setIsToolModalOpen(false);
  };

  /**
   * Close the unit content modal
   */
  const closeUnitContentModal = () => {
    setIsUnitContentModalOpen(false);
    setIsNewUnitModalOpen(false);
  };

  /**
   * Delete a unit from the database and remove it from the page display
   */
  const handleModalConfirmUnitDelete = async () => {
    try {
      const res = await axios.put(
        `/learning_units/deactivate/${learningToolId}/${activeModalUnit}`
      );

      if (res.status === 200 && res.data.success) {
        setToolUnits((prevUnits) =>
          prevUnits.filter((unit) => unit.learning_unit_id !== activeModalUnit)
        );
      } else {
        console.error("Error - unit was not deleted.");
      }
    } catch (error) {
      console.error("Error - unit was not deleted.");
    }

    closeUnitModal();
  };

  /**
   * Delete the learning tool from the database and remove it from the page.
   */
  const handleModalConfirmToolDelete = async () => {
    try {
      const res = await axios.put(
        `/learning_tools/deactivate/${learningToolId}`
      );

      if (res.status === 200 && res.data.success) {
        setToolUnits([]);
      } else {
        console.error("Error - tool was not deleted.");
      }
    } catch (error) {
      console.error("Error - tool was not deleted.");
    }

    closeToolModal();
  };

  /**
   * Open the unit content modal to allow the user to edit a unit data
   * @param {*} unitId - the id of the unit to edit
   * @param {*} idx - the index of the unit in the learning tool
   */
  const handleEditUnit = (unitId, idx) => {
    setActiveContentModalUnit(unitId);
    setActiveUnitIndex(idx);
    setIsUnitContentModalOpen(true);
  };

  /**
   * Update the page display with an updated unit data
   * @param {*} updatedUnitContent - object with the updated unit
   */
  const handleUnitContentConfirm = async (updatedUnitContent) => {
    try {
      setToolUnits((prevUnits) =>
        prevUnits.map((unit) =>
          unit.learning_unit_id === activeContentModalUnit
            ? { ...unit, ...updatedUnitContent }
            : unit
        )
      );
    } catch (error) {
      console.error("Error - unit was not updated.", error);
    }

    closeUnitContentModal();
  };

  /**
   * Add a new unit to the tool
   * @param {*} newUnit - object with the new unit data
   */
  const handleAddUnitToList = async (newUnit) => {
    try {
      setToolUnits((prevUnits) => [...prevUnits, newUnit.newUnit]);
    } catch (error) {
      console.error("Error - unit was not added to list.", error);
    }

    closeUnitContentModal();
  };

  if (toolUnits.length === 0) {
    return null;
  }

  const vocabularyTable = toolUnits.map((unit, idx) => {
    const basicUrl = `${apiUrl}`;

    const audioUrl = unit.resource_url
      ? `${basicUrl}/${unit.resource_url}`
      : null;

    return (
      <tr key={idx}>
        <td>{unit.vocab_orig_text}</td>
        <td>{unit.vocab_translated_text}</td>
        <td>
          {audioUrl ? (
            <AudioPlayer audioUrl={audioUrl} />
          ) : (
            <span>No Audio</span>
          )}
        </td>
        <td className={styles.iconsTd}>
          <LearningUnitButtons
            mayEditTool={mayEditTool}
            isVoc={true}
            unitId={unit.learning_unit_id}
            onEditUnit={() => handleEditUnit(unit.learning_unit_id, idx)}
            onDeleteUnit={onDeleteUnit}
          />
        </td>
      </tr>
    );
  });

  return (
    <div className={styles.vocabularyTool}>
      <div className={styles.toolButtons}>
        {mayEditTool && (
          <LearningToolButtons
            toolId={learningToolId}
            handleAddNewUnit={onAddUnit}
            onDeleteTool={onDeleteTool}
            maxUnitsPerTool={maxUnitsPerTool}
            unitsNum={toolUnits.length}
          />
        )}
      </div>
      <h2>Vocabulary</h2>
      <p>By {tool.username}</p>
      <table>
        <thead>
          <tr>
            <th>Text</th>
            <th>Translated Text</th>
            <th>Pronunciation</th>
            <th className={styles.iconsTh}></th>
          </tr>
        </thead>
        <tbody>{vocabularyTable}</tbody>
      </table>

      <LearningToolModal
        message="Are you sure you want to delete this learning unit?"
        approveBtnText="YES, DELETE"
        cancelBtnText="No, cancel"
        show={isUnitModalOpen}
        onClose={closeUnitModal}
        onConfirm={handleModalConfirmUnitDelete}
      />

      <LearningToolModal
        message="Are you sure you want to delete this learning tool?"
        approveBtnText="YES, DELETE"
        cancelBtnText="No, cancel"
        show={isToolModalOpen}
        onClose={closeToolModal}
        onConfirm={handleModalConfirmToolDelete}
      />

      <VocabularyModal
        header={`Edit Vocabulary - Item ${activeUnitIndex + 1}`}
        unitContent={toolUnits.find(
          (unit) => unit.learning_unit_id === activeContentModalUnit
        )}
        approveBtnText="Update"
        cancelBtnText="Cancel"
        show={isUnitContentModalOpen}
        onClose={closeUnitContentModal}
        onConfirm={handleUnitContentConfirm}
      />
      <VocabularyModal
        header={`Add Vocabulary Unit`}
        unitContent={null}
        learningToolId={learningToolId}
        approveBtnText="Create"
        cancelBtnText="Cancel"
        show={isNewUnitModalOpen}
        onClose={closeUnitContentModal}
        onConfirm={handleAddUnitToList}
      />
    </div>
  );
};

export default LearningToolVocabulary;
